import { useCallback, useEffect, useState } from 'react'
import { Plus } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchIncidents,
  fetchIncident,
  createIncident,
  addIncidentUpdate,
  resolveIncident,
  SEVERITIES,
  STATUSES,
  SEV_LABEL,
  STATUS_LABEL,
  INC_STR as S,
  type IncidentRow,
  type IncidentDetail,
} from './incidentsApi'

const sevColor: Record<string, string> = {
  sev1: 'text-danger',
  sev2: 'text-warning',
  sev3: 'text-secondary',
  sev4: 'text-tertiary',
}
const statusPill: Record<string, string> = {
  open: 'bg-danger/10 text-danger',
  investigating: 'bg-warning/10 text-warning',
  monitoring: 'bg-accent/10 text-accent',
  resolved: 'bg-success/10 text-success',
}

export function IncidentsScreen() {
  const { toast } = useToast()
  const [rows, setRows] = useState<IncidentRow[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const [sel, setSel] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchIncidents('', ac.signal)
      .then((r) => setRows(r))
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  return (
    <>
      <PageHeader
        title={S.incidents}
        screenId="A17.24"
        description={S.incidentsDesc}
        actions={
          <button
            type="button"
            onClick={() => {
              setCreating(true)
              setSel(null)
            }}
            className="flex items-center gap-1 rounded-md bg-accent px-3 py-1 text-sm text-white"
          >
            <Plus size={14} />
            {S.create}
          </button>
        }
      />
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Chap: ro'yxat */}
        <aside className="w-72 shrink-0 overflow-auto border-r border-border-default bg-surface-2">
          {err ? (
            <div className="p-3">
              <ErrorState code={err.code} onRetry={reload} />
            </div>
          ) : !rows ? (
            <div className="space-y-1.5 p-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.empty} />
          ) : (
            <ul>
              {rows.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSel(r.id)
                      setCreating(false)
                    }}
                    className={`block w-full border-b border-border-subtle px-3 py-2.5 text-left hover:bg-surface-3 ${
                      sel === r.id ? 'bg-surface-3' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm text-primary">{r.title}</span>
                      <span className={`shrink-0 font-mono text-xs ${sevColor[r.severity] ?? ''}`}>
                        {r.severity.toUpperCase()}
                      </span>
                    </div>
                    <span className={`mt-1 inline-block rounded px-1.5 py-0.5 text-xs ${statusPill[r.status] ?? ''}`}>
                      {STATUS_LABEL[r.status] ?? r.status}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* O'ng: detal / yaratish */}
        <div className="min-w-0 flex-1 overflow-auto">
          {creating ? (
            <CreateForm
              onDone={(id) => {
                setCreating(false)
                reload()
                setSel(id)
              }}
              onCancel={() => setCreating(false)}
            />
          ) : sel ? (
            <IncidentDetailPanel id={sel} onChanged={reload} />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-tertiary">{S.select}</div>
          )}
        </div>
      </div>
    </>
  )

  function CreateForm({ onDone, onCancel }: { onDone: (id: number) => void; onCancel: () => void }) {
    const [title, setTitle] = useState('')
    const [severity, setSeverity] = useState('sev3')
    const [summary, setSummary] = useState('')
    const [busy, setBusy] = useState(false)
    const submit = async () => {
      if (!title.trim()) return
      setBusy(true)
      try {
        const { id } = await createIncident(title.trim(), severity, summary.trim())
        toast(S.done, { tone: 'success' })
        onDone(id)
      } catch {
        toast(S.failed, { tone: 'error' })
      } finally {
        setBusy(false)
      }
    }
    return (
      <div className="mx-auto max-w-xl space-y-3 p-6">
        <h2 className="text-md font-medium text-primary">{S.create}</h2>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={S.title}
          className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
        />
        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary"
        >
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {SEV_LABEL[s]}
            </option>
          ))}
        </select>
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder={S.summary}
          rows={4}
          className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={submit}
            disabled={busy || !title.trim()}
            className="rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {S.create}
          </button>
          <button type="button" onClick={onCancel} className="rounded-md border border-border-default px-4 py-2 text-sm text-secondary">
            {S.cancel}
          </button>
        </div>
      </div>
    )
  }

  function IncidentDetailPanel({ id, onChanged }: { id: number; onChanged: () => void }) {
    const [inc, setInc] = useState<IncidentDetail | null>(null)
    const [dErr, setDErr] = useState<ApiError | null>(null)
    const [dTick, setDTick] = useState(0)
    const [note, setNote] = useState('')
    const [noteStatus, setNoteStatus] = useState('')
    const [rootCause, setRootCause] = useState('')
    const [actionItems, setActionItems] = useState('')
    const [busy, setBusy] = useState(false)
    const dReload = () => {
      setDTick((t) => t + 1)
      onChanged()
    }

    useEffect(() => {
      const ac = new AbortController()
      setDErr(null)
      setInc(null)
      fetchIncident(id, ac.signal)
        .then(setInc)
        .catch((e) => {
          if (!ac.signal.aborted) setDErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
        })
      return () => ac.abort()
    }, [id, dTick])

    const postUpdate = async () => {
      if (!note.trim()) return
      setBusy(true)
      try {
        await addIncidentUpdate(id, note.trim(), noteStatus)
        setNote('')
        setNoteStatus('')
        toast(S.done, { tone: 'success' })
        dReload()
      } catch {
        toast(S.failed, { tone: 'error' })
      } finally {
        setBusy(false)
      }
    }
    const doResolve = async () => {
      setBusy(true)
      try {
        await resolveIncident(id, rootCause.trim(), actionItems.trim())
        toast(S.done, { tone: 'success' })
        dReload()
      } catch {
        toast(S.failed, { tone: 'error' })
      } finally {
        setBusy(false)
      }
    }

    if (dErr) return <div className="p-6"><ErrorState code={dErr.code} onRetry={dReload} /></div>
    if (!inc) return <div className="space-y-2 p-6">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}</div>

    return (
      <div className="mx-auto max-w-2xl space-y-4 p-6">
        <div>
          <div className="flex items-center gap-2">
            <span className={`font-mono text-sm ${sevColor[inc.severity] ?? ''}`}>{inc.severity.toUpperCase()}</span>
            <span className={`rounded px-1.5 py-0.5 text-xs ${statusPill[inc.status] ?? ''}`}>
              {STATUS_LABEL[inc.status] ?? inc.status}
            </span>
          </div>
          <h2 className="mt-1 text-lg font-semibold text-primary">{inc.title}</h2>
          <p className="mt-0.5 text-xs text-tertiary">
            {S.started}: {new Date(inc.started_at).toLocaleString()}
            {inc.resolved_at && ` · ${S.resolved}: ${new Date(inc.resolved_at).toLocaleString()}`}
          </p>
          {inc.summary && <p className="mt-2 text-sm text-secondary">{inc.summary}</p>}
        </div>

        {(inc.root_cause || inc.action_items) && (
          <div className="rounded-lg border border-border-default bg-surface-2 p-3">
            <p className="text-xs font-medium text-secondary">{S.postmortem}</p>
            {inc.root_cause && <p className="mt-1 text-sm text-primary"><b>{S.rootCause}:</b> {inc.root_cause}</p>}
            {inc.action_items && <p className="mt-1 text-sm text-primary"><b>{S.actionItems}:</b> {inc.action_items}</p>}
          </div>
        )}

        {/* Timeline */}
        <div>
          <p className="mb-2 text-xs font-medium text-secondary">{S.timeline}</p>
          <ol className="space-y-2 border-l border-border-default pl-4">
            {inc.updates.length === 0 && <li className="text-sm text-tertiary">—</li>}
            {inc.updates.map((u, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[21px] top-1 h-2 w-2 rounded-full bg-accent" />
                <p className="text-sm text-primary">{u.note}</p>
                <p className="text-xs text-tertiary">
                  {u.status && `[${STATUS_LABEL[u.status] ?? u.status}] `}
                  {u.created_by && `${u.created_by} · `}
                  {new Date(u.created_at).toLocaleString()}
                </p>
              </li>
            ))}
          </ol>
        </div>

        {inc.status !== 'resolved' && (
          <>
            {/* Add update */}
            <div className="space-y-2 rounded-lg border border-border-default p-3">
              <p className="text-xs font-medium text-secondary">{S.addUpdate}</p>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={S.note}
                className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
              />
              <div className="flex gap-2">
                <select
                  value={noteStatus}
                  onChange={(e) => setNoteStatus(e.target.value)}
                  className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
                >
                  <option value="">— {S.status} —</option>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={postUpdate}
                  disabled={busy || !note.trim()}
                  className="rounded-md bg-accent px-3 py-1.5 text-sm text-white disabled:opacity-50"
                >
                  {S.add}
                </button>
              </div>
            </div>

            {/* Resolve */}
            <div className="space-y-2 rounded-lg border border-border-default p-3">
              <p className="text-xs font-medium text-secondary">{S.resolveBtn}</p>
              <input
                value={rootCause}
                onChange={(e) => setRootCause(e.target.value)}
                placeholder={S.rootCause}
                className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
              />
              <input
                value={actionItems}
                onChange={(e) => setActionItems(e.target.value)}
                placeholder={S.actionItems}
                className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
              />
              <button
                type="button"
                onClick={doResolve}
                disabled={busy}
                className="rounded-md bg-success px-3 py-1.5 text-sm text-white disabled:opacity-50"
              >
                {S.resolveBtn}
              </button>
            </div>
          </>
        )}
      </div>
    )
  }
}
